import {IInfo, IInfoBroker, IInfoConsumer} from "lib/commons/infos";
import {ILastDatasBuilder, JLastDatas} from "lib/commons/lastDatas";
import {IReg, IRegPointer, REG} from 'lib/commons/registry';
import {BaseAreaView, OBaseAreaViewInit} from "lib/commons/views";
import {DOM, JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {ESrcField, JSrcFields, JSrcFieldsTree, JSrcIdent, JSubItem, SRC, srcRef, srcUri} from "lib/wsp/src";
import {IWspUiEnv, JWspUriChangeMsg, WSP, Wsp} from "lib/wsp/wsp";
import {EWspChangesEvts, WspsLivePlace} from "lib/wsp/wspsLive";
import {InfoCurrentItem, InfoHighlighItemSgn, InfoReqCurrentItem, IShortDescCtx, ITEM, JItemNetLnkResp, JItemNetResp, srcRefSub} from "lib/wsp/item";
import {SrcBtn, SRCDRAWER, SrcDrawer, SrcPointer, SrcPointerDblClick, SrcPointerFastSelect, SrcPointerSetterAction} from "back/wsp/widgets/srcDrawer";
import {ActionBtn, Button, ButtonToggle, OButtonToggleInit} from "back/commons/widgets/buttons";
import {AccelKeyMgr, ACTION, Action, ActionSeparator, EButtonUiContext, IAccelKeyMgrPointer, IAction} from "lib/commons/actions";
import {POPUP} from "back/commons/widgets/popups";
import {OpenFastFindItem} from "back/wsp/actions/srcActions";
import {Grid, OGridSmallInit} from "back/commons/widgets/grid-tags";
import {EFolderState, GridColTreeDef, GridDataHolderJsonTree, GridDataRowJsonTree} from "back/commons/widgets/tree";
import {GridColDef, IGrid, IGridDataRow, IGridLineDrawer} from "back/commons/widgets/grid-core";
import {CellBuilderSrcIconCode, JSrcFieldsPointer, redrawSrcLine} from "back/wsp/widgets/srcGridColumns";
import {GridDataRowJson} from "back/commons/widgets/grid-libs";
import {Bookmarks} from "back/wsp/views/bookmarks";
import {IActionable, IContextMenuActionsPointer, IFocusActionables} from "back/commons/actionables";


/**
 */
export interface NetItems extends BaseAreaView<IRegPointer<IWspUiEnv>> {
	initialize(init: ONetItemsInit): this
}

export interface ONetItemsInit extends OBaseAreaViewInit<IRegPointer<IWspUiEnv>> {
	/** Pour dispatcher les demandes de focus / sélection. */
	infoBroker?: IInfoBroker;

	/** Réagira aux infos issues de ce broker (item courant, highlight d'items selon signature) */
	itemHandlingReact?: IInfoBroker

	/** Liste des confs possibles. Si moins de 2 confs, aucun de menu de sel de la conf n'est affiché. */
	confs?: JNetItemConf[]

	/** Masque l'entête avec le ptrItem du point de départ. */
	hidePtrItemFrom?: boolean

	/** Redéfinition de la colonne primaire. */
	primaryCol?: GridColTreeDef

	/** Colonnes secondaires à ajouter du grid. */
	secondaryCols?: GridColDef[]

	defaultAction?: IAction<IShortDescCtx>;

	/** Actions pour le menuContextuel. */
	actions?: IAction<IShortDescCtx>[];

	accelKeyMgr?: AccelKeyMgr<IShortDescCtx>;

	lastDatas?: JLDNetItems
}

interface JNetItemState {
	root?: srcRefSub
	dir?: 'asc' | 'desc' //si non spécifié dans la conf
	conf: JNetItemConf
}

export interface JNetItemConf {
	/** Conf issue du modèle pour lastDatas. */
	code?: string

	/** Libellé localisé de cette configuration (pour affichage dans le menu de choix). */
	label?: string

	/** Formule de restruction sur liens à parcourir (cf eu.scenari.wsp.item.LinkNameFilter). */
	linksAllowed?: string

	/** Requete de type 'match' sur les items cibles (xml de tage racine <match>, cf com.scenari.src.feature.search.SrcFeatureSearch). */
	itemTargeted?: string

	/** Si renseigné, impose la dir et élimine le choix à l'utilisateur. */
	dir?: 'desc' | 'asc'

	/**
	 * Affichage des subItems qui sont au départ des liens au sein de l'item ou subItem parent :
	 * - folded : replier, aucun affichage des subItems à l'origine des liens (par défaut)
	 * - flat : affichage à plat des subItems à l'origine des liens
	 * - unfolded : affichage des subItems à l'origine des liens en affichant la hiérarchie des subItems ancêtres.
	 *
	 * Utilisé UNIQUEMENT si dir=="desc"
	 */
	fromSubItems?: 'folded' | 'flat' | 'unfolded'
}

interface JLDNetItems {
	histo: JLDNetItemsHisto[]
}

interface JLDNetItemsHisto {
	root: srcRefSub
	conf: string | JNetItemConf
	dir?: 'asc' | 'desc' //si choisi par le user
}

export class NetItems extends BaseAreaView<IRegPointer<IWspUiEnv>> implements IShortDescCtx, IInfoConsumer, ILastDatasBuilder, IFocusActionables<IShortDescCtx>, IContextMenuActionsPointer<IShortDescCtx>, IAccelKeyMgrPointer<IShortDescCtx>, IGridLineDrawer {
	reg: IReg<IWspUiEnv>;
	wsp: Wsp;

	static CONF_ALL_CODE = "_all";

	confs: JNetItemConf[];
	infoBroker: IInfoBroker;
	itemHandlingReact: IInfoBroker;

	dataHolder = new NetItemsDataHolder(this);

	actions?: IAction<IShortDescCtx>[];
	accelKeyMgr?: AccelKeyMgr<IShortDescCtx>;

	/** boutons à afficher dans la focusToolbar. */
	focusActionables: IActionable<IShortDescCtx>[];

	get ctxMenuActions() {return {actions: this.actions, actionContext: this, rect: this._grid.getSelRect()}}

	/** Sélection en cours des src nodes dédoublonnés */
	shortDescs: JSrcFieldsTree[] = [];

	get emitter() {return this._grid} //on pointe le noeud focusable.

	protected netState: JNetItemState;
	protected histo: JNetItemState[];

	/** widgets html */
	protected _histoBtn?: Button;
	protected _rootSrc?: SrcPointer;
	protected _dirAscBtn?: ButtonToggle;
	protected _dirDescBtn?: ButtonToggle;
	protected _confsSelect?: HTMLSelectElement;
	protected _refreshBtn?: Button;
	protected _ascSkin?: HTMLStyleElement;
	protected _descSkin?: HTMLStyleElement;
	protected _activeSkin?: HTMLStyleElement;

	protected _grid: Grid;

	/** SrcUri actuellement "courant" (InfoCurrentItem) pour le repérer dans l'arbre */
	protected _currentSrcUri: srcUri;
	protected _highlightSgn: RegExp;
	protected _assigned: srcRef;

	setRootSrcRef(srcRef: srcRefSub) {
		if (this.netState.root == srcRef && this.initialized) return;
		this.netState = Object.assign({}, this.netState);
		this.netState.root = srcRef;
		if (srcRef) {
			const idx = this.histo.findIndex(v => v.root === srcRef);
			if (idx >= 0) this.histo.splice(idx, 1);
			this.histo.unshift(this.netState);
			if (this.histo.length > 12) this.histo.length = 12;
		}
		this._rootSrc?.setSrcRef(srcRef);
		this.dataHolder.rebuildFromRoot(true);
	}

	/** info synchrone */
	getRootSrcRef(): srcRefSub {
		return this.netState.root;
	}

	/** info dispo que quand root chargé. */
	getRootSrcUri(): srcUri | null {
		return null; //TODO from datas
		// const sd = this._rootSrc.shortDescs[0];
		// return sd ? sd.srcUri : null;
	}

	setNetConf(conf: JNetItemConf) {
		this.netState.conf = conf;
		if (conf.dir) this.netState.dir = undefined;
		this.refresh();
		this.dataHolder.rebuildFromRoot(true);
	}

	getNetConf() {return this.netState.conf}

	setDir(dir: "desc" | "asc") {
		if (this.netState.conf.dir) return;
		this.netState.dir = dir === "asc" ? "asc" : "desc";
		this.refresh();
		this.dataHolder.rebuildFromRoot(true);
	}

	getDir(): "desc" | "asc" {return this.netState.conf.dir || this.netState.dir || "desc"}

	async ensureRowVisible(srcUri: srcUri) {
		//TODO ensureRowVisible
	}

	protected _initialize(init: ONetItemsInit) {
		super._initialize(init); //init this.reg, this.area, this.areaContext
		this.wsp = this.reg.env.wsp;
		this.infoBroker = init.infoBroker;
		this.itemHandlingReact = init.itemHandlingReact;
		this.confs = init.confs && init.confs.length > 0 ? init.confs : [{}];

		//Si on est dans le mode fixedConf non modifiable.
		const fixedConf = this.confs.length < 2 ? this.confs[0] : null;

		// Ajout de l'option "aucun filtre" si plus d'un choix
		if (this.confs?.length >= 2 && !this.confs.find(entry => entry.code === NetItems.CONF_ALL_CODE))
			this.confs.unshift({code: NetItems.CONF_ALL_CODE, label: "Aucun filtre"});

		if ((init.lastDatas as JLDNetItems)?.histo) {
			const histo = (init.lastDatas as JLDNetItems).histo;
			if (histo.length > 0) {
				const buildState = (ld: JLDNetItemsHisto): JNetItemState => {
					const conf = fixedConf || (typeof ld.conf === "string" ? this.confs.find((c) => c.code === ld.conf) || this.confs[0] : ld.conf)
					return {
						root: ld.root,
						conf,
						dir: conf.dir ? undefined : ld.dir //si la conf impose une dir
					}
				}
				this.netState = buildState(histo[0]);
				this.histo = [];
				for (let i = 1; i < histo.length; i++) this.histo.push(buildState(histo[i]));
			}
		}
		if (!this.netState) this.netState = {conf: fixedConf || this.confs[0]};
		if (!this.histo) this.histo = [];

		//Construction Ui
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		this.reg.installSkin('scroll/small', sr);

		const headBar = sr.appendChild(<div id="headBar"/>);

		if (!init.hidePtrItemFrom) {
			this._histoBtn = <Button title="Historique des dernières racines sélectionnées" î={{
				reg: this.reg,
				icon: "/@skin@/wsp/views/netItems/histo.svg"
			}} onclick={this.onClickHisto}/> as Button
			this._rootSrc = new SrcPointer().initialize({
				reg: this.reg,
				draggable: true,
				dropable: true,
				tpl: SRCDRAWER.itemCodeAloneTpl,
				tplOver: SRCDRAWER.detailsPreviewTpl,
				place: this.reg.env.place,
				defaultAction: this.reg.getSvc("ptrItemDblClick", SrcPointerDblClick.SINGLETON),
			});
			this._rootSrc.classList.add("fixedIconSize");
			this._rootSrc.onSrcRefChange.add((drawer: SrcDrawer) => {
				this.setRootSrcRef(drawer.srcRefSub);
			});
			headBar.appendChild(<div id="rootSel">{this._rootSrc}{this._histoBtn}</div>);
		}

		if (!fixedConf || !fixedConf.dir) {
			//Ajout des btns de choix de dir.
			this._dirDescBtn = <ButtonToggle title="Réseau descendant" î={{
				reg: this.reg,
				icon: "/@skin@/wsp/views/netItems/descBtn.svg"
			} as OButtonToggleInit} onclick={this.onDescBtn}/> as ButtonToggle
			this._dirAscBtn = <ButtonToggle title="Réseau ascendant" î={{
				reg: this.reg,
				icon: "/@skin@/wsp/views/netItems/ascBtn.svg"
			} as OButtonToggleInit} onclick={this.onAscBtn}/> as ButtonToggle
		}

		if (!fixedConf) {
			//Ajout du Select de conf
			this._confsSelect = <select/> as HTMLSelectElement;
			for (const conf of this.confs) {
				this._confsSelect.appendChild(<option label={conf.label} selected={conf === this.netState.conf ? "" : undefined}/>);
			}
			this._confsSelect.onchange = this.onSelectConf;
		}

		if (!fixedConf || fixedConf.dir !== "desc") {
			//Ajout du bouton refresh
			this._refreshBtn = <Button title="Rafraîchir" î={{
				reg: this.reg,
				icon: "/@skin@/commons/icons/refresh.svg"
			}} onclick={this.onRefresh}/> as Button
		}
		headBar.appendChild(<div id="options">{this._dirDescBtn}{this._dirAscBtn}{this._confsSelect}{this._refreshBtn}</div>);

		const colDefs: GridColDef[] = [init.primaryCol || new GridColTreeDef('srcTree')
			.setDefaultSort(1, 'ascendant')
			.setFlex('1rem', 1, 1).setMinWidth('55px').setSortable(true)
			.setCellBuilder(new CellBuilderNet(this.reg, this.wsp.wspMetaUi, false, this.wsp.srcUriItemsSortFn).setNetItems(this))];
		if (init.secondaryCols) colDefs.push(...init.secondaryCols);

		this._grid = sr.appendChild(<c-grid î={{
			selType: 'multi',
			reg: this.reg,
			columnDefs: colDefs,
			dataHolder: this.dataHolder,
			hideHeaders: true,
			noResizableCol: true,
			autoSelOnFocus: 'first',
			skinScroll: "scroll/small",
			defaultAction: init.defaultAction,
			defaultActionCtx: this,
			lineDrawer: this
		} as OGridSmallInit}/>) as Grid;
		if (init.secondaryCols) DOM.setAttrBool(this._grid, "data-has-secondary-cols", true);
		this.reg.installSkin('wsp-src-grid', this._grid.shadowRoot);

		this._grid.addEventListener('grid-select', function (this: IGrid, ev: Event) {
			const netItems = DOMSH.findHost<NetItems>(this);
			const sel = netItems.dataHolder.getSelectedDatas();
			netItems.shortDescs = Array.from(sel.reduce((set, node) => {
				if (node.src) set.add(node.src);
				return set;
			}, new Set<JSrcFields>()));
			//console.log("bookmarks.shortDescs::::", bk.shortDescs);
			if (this.matches(':focus')) this.dispatchEvent(new CustomEvent<IFocusActionables<IShortDescCtx>>('c-focus-actions', {detail: netItems, bubbles: true, composed: true}));
		});

		this._grid.addEventListener('focus', function (this: IGrid, ev: Event) {
			this.dispatchEvent(new CustomEvent<IFocusActionables<IShortDescCtx>>('c-focus-actions', {detail: DOMSH.findHost(this) as Bookmarks, bubbles: true, composed: true}));
		});

		if (init.accelKeyMgr) {
			this.accelKeyMgr = init.accelKeyMgr;
			this._grid.addEventListener('keydown', (ev: KeyboardEvent) => {
				this.accelKeyMgr.handleKeyboardEvent(ev, this);
			});
		}

		let actions = init.actions;
		if (actions) {
			actions = ACTION.injectSepByGroup(actions, this.reg.getPref("groupOrder.wsp.shortDesc", ""), this);
			this.actions = actions;
			this.focusActionables = ActionBtn.buildButtons(actions, this, 'bar');
		}

		if (init.itemHandlingReact) {
			const req = new InfoReqCurrentItem();
			init.itemHandlingReact.dispatchInfo(req, this);
			if (req.srcUri) this._setCurrentItem(req.srcUri);
		}

		this._grid.linesNode.setAttribute("draggable", "true");
		this._grid.linesNode.addEventListener('dragstart', function (ev: DragEvent) {
			//console.log(ev);
			const itemNets = DOMSH.findHost<NetItems>(DOMSH.findHost<Grid>(this));
			if (itemNets.shortDescs.length > 0) ITEM.setShortDescTransferToDragSession(itemNets, ev, 'build');
			else ev.preventDefault();
		});
		this._grid.linesNode.addEventListener('dragend', ITEM.resetShortDescTransferToDragSession);

		//fetch
		this.setRootSrcRef(this.netState.root);
	}

	protected _refresh() {
		if (this._dirAscBtn) {
			if (this.netState.conf.dir) {
				DOM.setHidden(this._dirAscBtn, true);
				DOM.setHidden(this._dirDescBtn, true);
			} else {
				DOM.setHidden(this._dirAscBtn, false);
				DOM.setHidden(this._dirDescBtn, false);
				const dir = this.getDir();
				this._dirAscBtn.toggleOn = dir === "asc";
				this._dirDescBtn.toggleOn = dir === "desc";
			}
		}
		const skin = this.getDir() === "asc" ? this._ascSkin || (this._ascSkin = this.reg.createSkin("wsp-net-items/grid/asc")) : this._descSkin || (this._descSkin = this.reg.createSkin("wsp-net-items/grid/desc"));
		if (this._activeSkin !== skin) {
			if (this._activeSkin) this._grid.shadowRoot.replaceChild(skin, this._activeSkin);
			else this._grid.shadowRoot.insertBefore(skin, this._grid.scrollNode);
			this._activeSkin = skin;
		}
	}


	setHighlightSgn(sgnPattern: RegExp, assigned: srcRef) {
		this._highlightSgn = sgnPattern;
		this._assigned = assigned;
		this._grid.invalidateRows();
	}

	redrawLine(row: GridDataRowJson<INetNode>, line: HTMLElement): void {
		const src = row.rowKey.src;
		if (src === null) {
			//"Aucun lien trouvé"
			line.classList.toggle('info', true);
		} else {
			line.classList.toggle('info', false);
			redrawSrcLine(this.wsp, row.rowKey.src, line, this._currentSrcUri, this._highlightSgn, this._assigned);
		}
	}


	/** Identifie l'item courant dans l'app. */
	_setCurrentItem(srcUri: srcUri | null) {
		this._currentSrcUri = srcUri;
		this._grid.invalidateRows();
		if (this._currentSrcUri) this.ensureRowVisible(this._currentSrcUri);
	}

	buildLastDatas?(parentLastDatas: JLastDatas): void {
		const lastDatasKey = this.getAttribute('last-datas');
		if (lastDatasKey) {
			const ld: JLDNetItems = {histo: []};
			if (this.netState.root) ld.histo.push({
				root: this.netState.root,
				dir: this.netState.dir,
				conf: this.netState.conf.code || this.netState.conf
			}); //state en cours valide
			if (this.histo) for (const h of this.histo) {
				ld.histo.push({
					root: h.root,
					dir: h.dir,
					conf: h.conf.code || h.conf
				});
			}
			if (ld.histo.length > 0) parentLastDatas[lastDatasKey] = ld;
		}
	}

	onInfo(info: IInfo): void {
		if (info instanceof InfoCurrentItem) {
			this._setCurrentItem(info.srcUri);
		} else if (info instanceof InfoHighlighItemSgn) {
			this.setHighlightSgn(info.sgnPattern, info.assigned);
		}
	}

	onViewShown() {
		if (this.itemHandlingReact) {
			this.itemHandlingReact.addConsumer(this);
			const req = new InfoReqCurrentItem();
			this.itemHandlingReact.dispatchInfo(req, this);
			this._setCurrentItem(req.srcUri);
			if (!this._onConnRenewed && !this.netState.root) {
				//1er init, on prend l'item courant par défaut.
				if (req.srcUri) this._rootSrc.setSrcRef(req.srcUri, req.shortDesc);
			}
		}
		if (!this._onConnRenewed) {
			//1er init
			this._onConnRenewed = this.onConnRenewed.bind(this);
			this.reg.env.place.eventsMgr.on("onConnectionRenewed", this._onConnRenewed);
			this._wspUriChange = this.onWspUriChange.bind(this);
			this.reg.env.place.eventsMgr.on("wspUriChange", this._wspUriChange);
		}
	}

	onViewHidden(closed?: boolean): void {
		if (this.itemHandlingReact) this.itemHandlingReact.removeConsumer(this);
		if (closed) {
			this.reg.env.place.eventsMgr.removeListener("onConnectionRenewed", this._onConnRenewed);
			this.reg.env.place.eventsMgr.removeListener("wspUriChange", this._wspUriChange);
		}
	}

	protected _wspUriChange: (msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') => void;

	protected onWspUriChange(msg: JWspUriChangeMsg, from: WspsLivePlace | 'local' | 'server') {
		if (msg.wspCd === this.wsp.code) this.dataHolder.refreshSrc(msg, msg.type === EWspChangesEvts.s);
	}

	protected _onConnRenewed: () => void;

	protected async onConnRenewed() {
		this.dataHolder.rebuildFromRoot(true, true);
	}

	protected async onClickHisto(this: HTMLElement, ev: MouseEvent) {
		const target = ev.target as HTMLElement; //pour mémoire après async
		const actions: IAction<SrcPointer>[] = [];
		const netItems = DOMSH.findHost<NetItems>(this);
		if (netItems.histo.length > 0) {
			const uris: srcRefSub[] = [];
			const curr = netItems._rootSrc.srcRefSub;
			for (let h of netItems.histo) if (h.root && h.root !== curr) uris.push(h.root);
			const srcs = await WSP.fetchShortDescs(netItems.wsp, netItems, ...uris);
			for (const src of srcs) {
				if (src && src.srcSt > 0 && src.itModel != null) actions.push(new SrcPointerSetterAction(src));
			}
		}
		if (actions.length === 0) actions.push(new Action<SrcPointer>('none').setEnabled(false).setLabel("Historique vide"));
		else actions.reverse();
		actions.push(
			new ActionSeparator(),
			new SrcPointerFastSelect("selOther" /*Autre id pour tuer accelKey*/).setLabel("Autre item...")
		);
		POPUP.showPopupActionsFromEvent({
			reg: netItems.reg,
			actions,
			actionContext: netItems._rootSrc
		}, ev, target);
	}

	protected onDescBtn(this: Button, ev: MouseEvent) {
		DOMSH.findHost<NetItems>(this).setDir("desc");
	}

	protected onAscBtn(this: Button, ev: MouseEvent) {
		DOMSH.findHost<NetItems>(this).setDir("asc");
	}

	protected onRefresh(this: Button, ev: MouseEvent) {
		DOMSH.findHost<NetItems>(this).dataHolder.rebuildFromRoot(true, true);
	}

	protected onSelectConf(this: HTMLSelectElement) {
		const netItems = DOMSH.findHost<NetItems>(this);
		netItems.setNetConf(netItems.confs[this.selectedIndex]);
	}
}

REG.reg.registerSkin("wsp-net-items", 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	:focus-visible:not(c-grid) {
		outline: var(--focus-outline);
	}

	[hidden] {
		display: none !important;
	}

	#headBar {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-wrap: wrap;
		border-bottom: 1px solid var(--border-color);
		padding: .2em;
	}

	#rootSel,
	#options {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex: 1 0 auto;
	}

	#options {
		justify-content: flex-end;
	}

	wsp-src-pointer {
		flex: 0 1 auto;
		margin: 0;
	}

	select {
		border: 1px solid var(--border-color);
		margin-inline: 2px;
		background-color: unset;
		color: var(--color);
		font-size: inherit;
	}

	c-grid {
		flex: 1;
		border: none;
		background-color: var(--row-bgcolor);
	}
`);


REG.reg.registerSkin("wsp-net-items/grid/desc", 1, /* language=CSS */ `
	x-twisty {
		padding-inline-start: 1.5em !important;
	}

	x-twisty.opened {
		background: url(/@skin@/wsp/views/netItems/descTwistyOpened.svg) no-repeat center / 1.5em !important;
	}

	x-twisty.closed {
		background: url(/@skin@/wsp/views/netItems/descTwistyClosed.svg) no-repeat center / 1.5em !important;
	}

	x-twisty.subItem.opened {
		background: url(/@skin@/wsp/views/netItems/subItemTwistyOpened.svg) no-repeat center / 1.5em !important;
	}

	x-twisty.subItem.closed {
		background: url(/@skin@/wsp/views/netItems/subItemTwistyClosed.svg) no-repeat center / 1.5em !important;
	}

	.info > * > x-twisty {
		visibility: hidden;
	}

	.info {
		color: var(--fade-color);
		font-style: italic;
		margin-inline-start: -1em;
	}

	/** Réservation de la largeur de background-image pour qu'il n'y ait pas de superposition avec les secondary-cols */
	:host([data-has-secondary-cols]) .line {
		padding-inline-end: .8rem;
	}

	.space {
		color: var(--alt1-color);
		font-style: italic;
		margin-inline-start: .3em;
	}
`);

REG.reg.registerSkin("wsp-net-items/grid/asc", 1, /* language=CSS */ `
	x-twisty {
		padding-inline-start: 1.5em !important;
	}

	x-twisty.opened {
		background: url(/@skin@/wsp/views/netItems/ascTwistyOpened.svg) no-repeat center / 1.5em !important;
	}

	x-twisty.closed {
		background: url(/@skin@/wsp/views/netItems/ascTwistyClosed.svg) no-repeat center / 1.5em !important;
	}

	.info > * > x-twisty {
		visibility: hidden;
	}

	.info {
		color: var(--fade-color);
		font-style: italic;
		margin-inline-start: -1em;;
	}

	.space {
		color: var(--alt1-color);
	  font-style: italic;
		margin-inline-start: .3em;
	}
`);

customElements.define("wsp-net-items", NetItems);

class GridDataRowNetItems extends GridDataRowJsonTree<INetNode> {
	getData(key: ESrcField): any {
		const src = this.rowDatas.src;
		return src ? src[key] : undefined;
	}
}

class NetItemsDataHolder extends GridDataHolderJsonTree<INetNode> {

	fieldsLnks: ESrcField[];
	fieldsFrom?: ESrcField[];

	/** Noeud racine des this._datas. */
	root: INetNode;

	/** Pour refresh, set des items listés (pas des subItems), en source et ou cible des liens. */
	protected _srcUsed: Set<srcRef> = new Set();

	/** Cache des itemNets recherchés */

	//protected _itemNetFetched: Map<srcRefSub, JItemNetResp> = new Map();

	/**
	 *
	 * @param netState todo abstraire via une interface INetState
	 */
	constructor(public netState: NetItems) {
		super('ch');
		this.setDefaultOpenState((node: INetNode) => (node as ISubNetNode).subItem != null);
	}

	protected newDataRow(): GridDataRowJsonTree<INetNode> {return new GridDataRowNetItems(this, null)}

	async openFolderAsync(rowKey: INetNode): Promise<boolean> {
		if (this.ch(rowKey) === null) {
			const srcRef = ITEM.srcRefSub(rowKey.src);
			//let itemNet = this._itemNetFetched.get(srcRef);
			this.setFolderSt(rowKey, EFolderState.opening);
			//if (!itemNet) {
			const wsp = this.netState.wsp;
			const conf = this.netState.getNetConf();
			const itemNet = await ITEM.fetchSrcNetJson(wsp, this.netState, srcRef, this.fieldsLnks,
				this.netState.getDir(), true, conf.linksAllowed, conf.itemTargeted, 100, 1, this.fieldsFrom
			);
			if (!this.isRowkeyAlive(rowKey)) return false; //racecond
			//this._itemNetFetched.set(srcRef, itemNet);
			//}
			const newNode: INetNode = {src: itemNet.src, ch: null};
			this.fillNodesFromItemNetResp(itemNet, newNode);
			this.setFolderSt(rowKey, EFolderState.closed);
			if (this.replaceRowKey(rowKey, newNode, false)) return super.openFolder(newNode);
			return false;
		}
		return this.openFolder(rowKey);
	}

	/** Appelé à l'init, à chaque chgt de config, en cas de perte de connexion. */
	async rebuildFromRoot(clearCaches?: boolean, preserveOpenStates?: boolean) {
		if (clearCaches) this.clearCaches();
		let oldTree = preserveOpenStates ? this.root : null;
		this.setDatas([]); //todo ifEmpty du grid
		const srcRef = this.netState.getRootSrcRef();
		if (!srcRef) return;
		//let itemNet = this._itemNetFetched.get(srcRef);
		//if (!itemNet) {
		const wsp = this.netState.wsp;
		const conf = this.netState.getNetConf();
		this.fieldsLnks = wsp.getShortDescFields();
		const dir = this.netState.getDir();
		this.fieldsFrom = dir === "desc" && (conf.fromSubItems === "flat" || conf.fromSubItems === "unfolded") ? this.fieldsLnks.concat("itSubItems") : undefined;
		const itemNet = await ITEM.fetchSrcNetJson(wsp, this.netState, srcRef, this.fieldsLnks,
			dir, true, conf.linksAllowed, conf.itemTargeted, 100, 1, this.fieldsFrom
		);
		if (srcRef !== this.netState.getRootSrcRef()) return; //raceCond
		//this._itemNetFetched.set(srcRef, itemNet);
		//}
		this.root = {src: itemNet.src, ch: null};
		this._srcUsed.add(SRC.srcRef(itemNet.src));
		this.fillNodesFromItemNetResp(itemNet, this.root);
		this.setDatas(this.root.ch || []);
		if (oldTree) {
			//On réouvre les noeuds
			const openNodes = async (oldNode: INetNode, newNode: INetNode) => {
				if (newNode.ch == null) return; // pas de fils pour newNode
				for (let oldN of oldNode.ch) {
					if (!oldN.src || this.folderSt(oldN) !== EFolderState.opened) continue;
					//oldN ouvert, on cherche newN
					const oldRef = SRC.srcRef(oldN.src);
					for (let newN of newNode.ch) {
						if (SRC.srcRef(newN.src) === oldRef) {
							await this.openFolderAsync(newN)
							newN = newNode.ch.find((n) => n.src && SRC.srcRef(n.src) === oldRef);
							await openNodes(oldN, newN);
							break;
						}
					}
				}
			}
			await openNodes(oldTree, this.root);
		}
	}

	refreshSrc(ident: JSrcIdent, stOnly?: boolean) {
		if (!this.root) return;
		const srcRef = SRC.srcRef(ident);
		if (!this._srcUsed.has(srcRef)) return; //item pas dans le réseau affiché
		const scanNode = (node: INetNode) => {
			if (node.src && SRC.srcRef(node.src) === srcRef) {
				if (node.ch === null) {
					//Fils non calculés, on ne refresh que le node.
					this.refreshSrcNode(node);
				} else if (this.netState.getDir() === "asc" || stOnly) {
					//Fils calculés, mais liens ascendants ou chgt de st uniquement
					// on ne refresh que le node.
					this.refreshSrcNode(node);
					// on poursuit le scan
					for (const ch of node.ch) scanNode(ch);
				} else {
					//Fils calculés et liens descendants
					if (node === this.root) {
						this.rebuildFromRoot(true, true);
					} else if (this.folderSt(node) === EFolderState.opened) {
						//on refresh l'itemNet complet.
						this.closeFolder(node);
						this.openFolderAsync(node);
					} else {
						//dossier clos, on purge le calcul des fils
						this.setCh(node, null); // à recalculer.
						this.setFolderSt(node, EFolderState.closed);
						this.refreshSrcNode(node);
					}
					return;
				}
			}
			if (node.ch) for (const ch of node.ch) scanNode(ch);
		}
		scanNode(this.root);
	}

	protected async refreshSrcNode(node: INetNode) {
		const fields = await WSP.fetchSrc(this.netState.wsp, this.netState, ITEM.srcRefSub(node.src), this.fieldsFrom || this.fieldsLnks);
		if (!this.isRowkeyAlive(node)) return; //raceCond
		node.src = fields;
		this.resetRowCache(node);
		this.grid?.invalidateRows(this.getOffset(node));
	}

	protected fillNodesFromItemNetResp(resp: JItemNetResp, toFill: INetNode) {
		//toFill.src = resp.src; //déjà renseigné
		toFill.net = resp;
		switch (this.netState.getDir() !== "desc" ? "folded" : this.netState.getNetConf().fromSubItems || "folded") {
		case "folded":
			//Pas de subItems affihcés en fils.
			toFill.lnks = resp.lnks.length > 0 ? resp.lnks : undefined;
			break;
		case "flat":
			for (const lnk of resp.lnks) {
				const subItem = ITEM.findSubItem(lnk.subIt, resp.src.itSubItems);
				if (!subItem) {
					if (!toFill.lnks) toFill.lnks = [];
					toFill.lnks.push(lnk);
				} else {
					if (!toFill.subNodes) toFill.subNodes = new Map<string, ISubNetNode>();
					let subNetNode: ISubNetNode = toFill.subNodes.get(lnk.subIt);
					if (!subNetNode) {
						subNetNode = {
							src: resp.src,
							subItem: subItem,
							lnks: [],
							ch: null //à calculer
						};
						toFill.subNodes.set(lnk.subIt, subNetNode);
					}
					subNetNode.lnks.push(lnk);
				}
			}
			break;
		case "unfolded":
			for (const lnk of resp.lnks) {
				const subItems = ITEM.findSubItemStack(lnk.subIt, resp.src.itSubItems);
				if (!subItems) {
					if (!toFill.lnks) toFill.lnks = [];
					toFill.lnks.push(lnk);
				} else {
					let curr = toFill;
					for (let i = 0; i < subItems.length; i++) {
						const subIt = subItems[i];
						if (!curr.subNodes) curr.subNodes = new Map<string, ISubNetNode>();
						let subNetNode: ISubNetNode = curr.subNodes.get(subIt.id);
						if (!subNetNode) {
							subNetNode = {
								src: resp.src,
								subItem: subIt,
								ch: null //à calculer
							};
							curr.subNodes.set(subIt.id, subNetNode);
						}
						if (i == subItems.length - 1) (subNetNode.lnks || (subNetNode.lnks = [])).push(lnk);
						curr = subNetNode;
					}
				}
			}
			break;
		}

		const buildCh = (node: INetNode) => {
			//Construction des nodes fils
			node.ch = [];
			// 1. SubItems
			if (node.subNodes) for (const subN of node.subNodes.values()) {
				node.ch.push(subN);
				buildCh(subN);
			}
			// 2. Links
			if (node.lnks) for (const lnk of node.lnks) {
				node.ch.push({
					src: lnk.src, //cible du lien
					ch: null //à calculer
				});
				this._srcUsed.add(SRC.srcRef(lnk.src));
			}
		}

		buildCh(toFill);
		if (toFill.ch.length === 0) {
			toFill.ch.push({src: null, ch: undefined});
		}
	}

	clearCaches() {
		this._srcUsed.clear();
		//this._itemNetFetched.clear();
	}
}

/** Noeud représentant un item ou un subItem racine cible d'un lien. */
interface INetNode {

	/**
	 * Src du noeud pour l'affichage des rows lorsqu'ils sont une racine d'une cible d'un lien.
	 * Cet src est soit issu de JItemNetLnkResp.src (cible du lien amont), soit d'un refresh lié à un update de l'item container.
	 *
	 * Si null, indique une absence de noeuds fils dans le réseau.
	 */
	src: JSrcFields | null

	/**
	 * Liens sortants (disponible en async, une fois chargés).
	 */
	net?: JItemNetResp

	/**
	 * En mode fromSubItems='folded', égal à this.net.lnks
	 * En mode fromSubItems='unfolded'|'flat',
	 * 	- Si ce noeud est une racine (pas un ISubNetNode) égal aux net.lnks qui ne partent pas d'un subItem (JItemNetLnkResp.subIt==null).
	 * 	- Si ce noeud est un ISubNetNode égal aux net.lnks qui partent de ce subItem (JItemNetLnkResp.subIt==this.subNode.id).
	 */
	lnks?: JItemNetLnkResp[]

	/**
	 * Map intermédiaire pour construire les noeuds fils this.ch
	 * En mode fromSubItems='folded', reste indéfini
	 * En mode fromSubItems='unfolded'|'flat' liste les subItems fils contenant des liens.
	 */
	subNodes?: Map<string, ISubNetNode>

	/**
	 * Fils issus de la concaténation des noeuds construits à partir de this.subNodes (ISubNetNode) et this.lnks.
	 * null = non calculé (this.net non fetché)
	 * undefined = pas de fils
	 */
	ch: undefined | null | INetNode[]
}

/**
 * Pour le mode fromSubItems='unfolded' | 'flat'.
 * Noeud représentant un subItem interne à INetNode.
 */
interface ISubNetNode extends INetNode {
	/**
	 * L'affichage doit se limiter aux infos dans ce ISubNetNode.subNode, les infos de son INetNode.src contenant
	 * sont déjà affichées dans les rows ancêtres.
	 */
	subItem: JSubItem
}

class CellBuilderNet extends CellBuilderSrcIconCode {
	protected netItems: NetItems;

	setNetItems(netItems: NetItems) {
		this.netItems = netItems;
		return this;
	}

	redrawCell(row: GridDataRowJson<any | INetNode>, root: HTMLElement): void {
		(root as JSrcFieldsPointer)._srcFields = (row.rowDatas as INetNode).src;
		let span = root.firstElementChild as HTMLElement;
		if (!span) span = this._buildContent(row, root);
		this.redrawIcon(row, span);
		//Code
		span = span.nextElementSibling as HTMLElement;
		DOM.setTextContent(span, this._getValue(row));
		//Space
		span = span.nextElementSibling as HTMLElement;
		DOM.setTextContent(span, this._getSpace(row));

		DOM.setAttr(root, "title", this._getDescription(row) || undefined);

		//On masque l'icone si row de type subItem
		const isSubItem = (row.rowDatas as ISubNetNode).subItem != null;
		DOM.setHidden(root.firstElementChild, isSubItem); //pas d'icone si subItem
		(root.previousElementSibling as HTMLElement).classList.toggle("subItem", isSubItem); //twisty != si subItem
	}

	protected _buildContent(row: IGridDataRow, root: HTMLElement): HTMLElement {
		//icon
		const iconSpan = super._buildContent(row, root);
		//code
		root.appendChild(document.createElement('span'));
		//space
		const space = root.appendChild(document.createElement('span'));
		space.classList.add("space");
		return iconSpan;
	}

	protected _getValue(row: GridDataRowJson<any | INetNode>): string {
		const node = row.rowDatas as INetNode;
		if ((node as ISubNetNode).subItem) return (node as ISubNetNode).subItem.ti;
		if (node.src === null) return this.netItems.getDir() === "asc" ? "Aucun item référençant" : "Aucun item référencé";
		return row.cacheHolder['code'] || (row.cacheHolder['code'] = this.srcRdr.getMainName(node.src, this.wspMetaUi));
	}

	protected _getIcon(row: GridDataRowJson<any | INetNode>): string | null {
		const node = row.rowDatas as INetNode;
		if ((node as ISubNetNode).subItem || node.src === null) return null;
		return row.cacheHolder['icon'] || (row.cacheHolder['icon'] = this.srcRdr.getIcon(node.src, this.wspMetaUi));
	}

	protected _getSpace(row: GridDataRowJson<any | INetNode>): string | null {
		const node = row.rowDatas as INetNode;
		if ((node as ISubNetNode).subItem || node.src === null) return null;
		const sp = ITEM.extractSpaceLabel(node.src.srcUri, null);
		return row.cacheHolder['space'] || (row.cacheHolder['space'] = sp == null ? null : `(${sp})`);
	}
}
